<!-- src/components/LineChart.vue -->
<template>
  <div class="line-chart-container">
    <canvas ref="canvas"></canvas>
  </div>
</template>

<script>
import {
  Chart,
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

// ① Import the DataLabels plugin
//import ChartDataLabels from 'chartjs-plugin-datalabels';

export default {
  name: 'LineChart',
  props: {
    labels:    { type: Array, required: true },
    datasets:  { type: Array, required: true },
    options:   { type: Object, default: () => ({}) }
  },
  data() {
    return {
      chart: null
    };
  },
  mounted() {
    // ② Register core elements/controllers AND the DataLabels plugin
    Chart.register(
        LineController,
        LineElement,
        PointElement,
        LinearScale,
        CategoryScale,
        Title,
        Tooltip,
        Legend,
        //ChartDataLabels              // ← register the plugin here
    );
    this.renderChart();
  },
  beforeDestroy() {
    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }
  },
  watch: {
    labels: 'updateChartData',
    datasets: {
      handler: 'updateChartData',
      deep: true
    },
    options(newOpts) {
      if (this.chart) {
        this.chart.options = newOpts;
        this.chart.update();
      }
    },
  },
  methods: {
    renderChart() {
      const ctx = this.$refs.canvas.getContext('2d');

      // ③ Build a default options object that includes datalabels
      const defaultOptions = {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          x: {
            type: 'category',          // ← use “category” instead of “linear”
            title: { display: true, text: 'Time' }
          },
          y: {
            beginAtZero: true,
            title: { display: true, text: 'Y Value' }
          }
        },
        plugins: {
          legend: { display: true, position: 'top' },
          tooltip: { mode: 'index', intersect: false },

          // ④ Configure the datalabels plugin here
          /*datalabels: {
            anchor: 'end',            // position label slightly above the point
            align: 'top',             // align label above
            color: '#444',            // font color
            font: {
              weight: '600',
              size: 12
            },
            formatter: (value) => {
              // If your dataset is just an array of numbers, `value` is that number.
              // If data points are objects like {x:..., y:...}, you can return `value.y`.
              return value;
            }
          }*/
        }
      };

      // ⑤ Merge defaultOptions with any incoming this.options
      const mergedOptions = {
        ...defaultOptions,
        ...this.options,
        scales: {
          // Keep our forced x/y settings, but allow overrides via this.options.scales
          x: {
            type: defaultOptions.scales.x.type,
            min: defaultOptions.scales.x.min,
            max: defaultOptions.scales.x.max,
            ...(this.options.scales?.x || {})
          },
          y: {
            beginAtZero: true,
            ...(this.options.scales?.y || {})
          }
        },
        plugins: {
          ...defaultOptions.plugins,
          // If the parent passed plugin overrides, merge them in.
          /*datalabels: {
            ...defaultOptions.plugins.datalabels,
            ...(this.options.plugins?.datalabels || {})
          }*/
        }
      };

      const config = {
        type: 'line',
        data: {
          labels: this.labels,
          datasets: this.datasets.map(ds => ({
            label: ds.label,
            data: ds.data,
            borderColor: ds.borderColor || 'rgba(75, 192, 192, 1)',
            backgroundColor: ds.backgroundColor || 'rgba(75, 192, 192, 0.2)',
            fill: ds.fill ?? false,
            tension: ds.tension ?? 0.4,
            pointRadius: ds.pointRadius ?? 3
          }))
        },
        options: mergedOptions
      };

      this.chart = new Chart(ctx, config);
    },
    updateChartData() {
      if (!this.chart) return;
      this.chart.data.labels = this.labels;
      this.chart.data.datasets = this.datasets.map(ds => ({
        label: ds.label,
        data: ds.data,
        borderColor: ds.borderColor || 'rgba(75, 192, 192, 1)',
        backgroundColor: ds.backgroundColor || 'rgba(75, 192, 192, 0.2)',
        fill: ds.fill ?? false,
        tension: ds.tension ?? 0.4,
        pointRadius: ds.pointRadius ?? 3
      }));
      this.chart.options = this.options
      this.chart.update();
    }
  }
};
</script>

<style scoped>
.line-chart-container {
  position: relative;
  width: 100%;
  height: 400px;
}
</style>
